/**
 * Image Thumbnail Component
 *
 * Reusable component for displaying image thumbnails with loading and error states
 * Follows SOLID principles:
 * - Single Responsibility: Only responsible for rendering image thumbnails
 * - Open/Closed: Open for extension via props, closed for modification
 */

import React, { useEffect, useState, useRef } from 'react';
import Image from 'next/image';
import {
  typographyColors,
  backgroundColors,
  spacing,
  borders,
  typography,
} from '@/design-system/utils';
import { useImageFetcher } from '@/hooks/useImageFetcher';

export interface ImageThumbnailProps {
  /**
   * Image URL or null
   */
  image: string | null;
  /**
   * Alt text for the image
   */
  alt: string;
  /**
   * Optional title for image fetching context
   */
  title?: string;
  /**
   * Optional description for image fetching context
   */
  description?: string;
  /**
   * Optional place name for image fetching context
   */
  placeName?: string;
  /**
   * Optional category for image fetching context
   */
  category?: string;
  /**
   * Width of the thumbnail
   * @default '62px'
   */
  width?: string;
  /**
   * Height of the thumbnail
   * @default '62px'
   */
  height?: string;
  /**
   * Border radius
   * @default borders.radius.lg
   */
  borderRadius?: string;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Additional inline styles
   */
  style?: React.CSSProperties;
  /**
   * Callback when image loads successfully
   */
  onImageLoad?: () => void;
  /**
   * Callback when image fails to load
   */
  onImageError?: (error: Error) => void;
}

/**
 * Image Thumbnail Component
 * Displays an image thumbnail with loading and error states
 */
export const ImageThumbnail: React.FC<ImageThumbnailProps> = ({
  image,
  alt,
  title,
  description,
  placeName,
  category,
  width = '62px',
  height = '62px',
  borderRadius = borders.radius.lg,
  className = '',
  style = {},
  onImageLoad,
  onImageError,
}) => {
  const [imageUrl, setImageUrl] = useState<string | null>(image);
  const [imageError, setImageError] = useState<boolean>(false);
  const [useFallbackImg, setUseFallbackImg] = useState<boolean>(false);
  const { fetchImage, isLoading, error: fetchError } = useImageFetcher();
  const requestInProgressRef = useRef<string | null>(null);

  // Debug: Log initial props
  useEffect(() => {
    console.log('🔍 [ImageThumbnail] Component initialized with props:', {
      image,
      imageUrl,
      alt,
      title,
      description,
      placeName,
      category,
      hasImageProp: !!image,
      imagePropType: typeof image,
      imagePropValue: image,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only log on mount

  // Debug: Log when image prop changes
  useEffect(() => {
    if (image !== imageUrl) {
      console.log('🔄 [ImageThumbnail] Image prop changed:', {
        oldImageUrl: imageUrl,
        newImage: image,
        currentImageUrl: imageUrl,
        willUpdate: image !== imageUrl,
      });
      if (image) {
        console.log(
          '🔄 [ImageThumbnail] Updating imageUrl state from prop:',
          image
        );
        setImageUrl(image);
        setImageError(false);
        setUseFallbackImg(false); // Reset fallback when new image is provided
      }
    }
  }, [image, imageUrl]);

  // Fetch image if not provided
  // CRITICAL: Requests are NEVER cancelled - they complete regardless of component lifecycle
  useEffect(() => {
    if (!imageUrl && title && !imageError) {
      // Create a unique key for this request to prevent duplicate calls from this component
      const requestKey = `${title}|${description || ''}|${placeName || ''}|${category || ''}`;

      // Skip if same request is already in progress
      if (requestInProgressRef.current === requestKey) {
        console.log(
          '🔄 [ImageThumbnail] Request already in progress, skipping:',
          {
            title,
            requestKey,
          }
        );
        return;
      }

      // Mark request as in progress
      requestInProgressRef.current = requestKey;

      console.log(
        '🖼️ [ImageThumbnail] Starting image fetch (will NOT be cancelled):',
        {
          title,
          requestKey,
        }
      );

      // Fetch image - request will complete even if component unmounts
      fetchImage(title, {
        description: description,
        placeName: placeName,
        category: category || 'Image',
      })
        .then(url => {
          // Only update state if this is still the current request
          if (requestInProgressRef.current === requestKey) {
            if (url) {
              setImageUrl(url);
              setImageError(false);
              console.log(
                '✅ [ImageThumbnail] Image fetched and state updated:',
                {
                  title,
                  hasUrl: !!url,
                }
              );
            }
            // Clear the in-progress flag
            requestInProgressRef.current = null;
          } else {
            console.log(
              '⏭️ [ImageThumbnail] Response received but request key changed, ignoring:',
              {
                title,
                currentKey: requestInProgressRef.current,
                responseKey: requestKey,
              }
            );
          }
        })
        .catch(err => {
          // Only handle error if this is still the current request
          if (requestInProgressRef.current === requestKey) {
            console.error('❌ [ImageThumbnail] Error fetching image:', {
              title,
              error: err?.message || err,
            });
            if (onImageError) {
              onImageError(err);
            }
            // Clear the in-progress flag
            requestInProgressRef.current = null;
          }
        });
    }

    // Cleanup: Clear in-progress flag if component unmounts or dependencies change
    // BUT: The actual request continues in the background and completes
    return () => {
      if (requestInProgressRef.current) {
        console.log(
          '🧹 [ImageThumbnail] Component cleanup (request continues in background):',
          {
            title,
            requestKey: requestInProgressRef.current,
          }
        );
        // Don't clear immediately - let the request complete
        // The request deduplication in the service will handle it
      }
    };
  }, [
    imageUrl,
    title,
    description,
    placeName,
    category,
    fetchImage,
    imageError,
    onImageError,
  ]);

  // Log fetch errors
  useEffect(() => {
    if (fetchError && onImageError) {
      onImageError(fetchError);
    }
  }, [fetchError, onImageError]);

  const displayImage = imageUrl && !imageError ? imageUrl : null;

  // Handle image load error
  const handleImageError = (
    event?: React.SyntheticEvent<HTMLImageElement, Event>
  ) => {
    const errorDetails = {
      imageUrl: displayImage || imageUrl,
      alt,
      title,
      category,
      eventType: event?.type,
      target: event?.target,
      currentSrc: event?.currentTarget?.currentSrc,
      naturalWidth: event?.currentTarget?.naturalWidth,
      naturalHeight: event?.currentTarget?.naturalHeight,
    };

    console.error(
      '❌ [ImageThumbnail] Next.js Image failed to load:',
      errorDetails
    );

    try {
      const urlToCheck = displayImage || imageUrl;
      console.error('❌ [ImageThumbnail] Error details:', {
        url: urlToCheck,
        isExternal: urlToCheck?.startsWith('http'),
        domain: urlToCheck ? new URL(urlToCheck).hostname : null,
      });
    } catch (urlError) {
      console.error('❌ [ImageThumbnail] Error parsing URL:', {
        url: displayImage || imageUrl,
        error: urlError,
      });
    }

    // Try fallback to regular img tag if Next.js Image fails
    if (!useFallbackImg && displayImage) {
      console.log(
        '🔄 [ImageThumbnail] Attempting fallback to regular <img> tag'
      );
      setUseFallbackImg(true);
      setImageError(false); // Reset error to try fallback
      return; // Don't set error state yet, try fallback first
    }

    const error = new Error(
      `Image failed to load: ${displayImage || imageUrl}`
    );
    if (onImageError) {
      onImageError(error);
    }
    setImageError(true);
    console.warn('⚠️ [ImageThumbnail] Both Next.js Image and fallback failed');
  };

  // Handle fallback img error
  const handleFallbackImgError = () => {
    console.error(
      '❌ [ImageThumbnail] Fallback <img> tag also failed to load:',
      {
        imageUrl: displayImage || imageUrl,
        alt,
        title,
      }
    );
    setImageError(true);
    const error = new Error(
      `Image failed to load (fallback also failed): ${displayImage || imageUrl}`
    );
    if (onImageError) {
      onImageError(error);
    }
  };

  // Handle fallback img load
  const handleFallbackImgLoad = () => {
    console.log('✅ [ImageThumbnail] Fallback <img> tag loaded successfully:', {
      imageUrl: displayImage || imageUrl,
      alt,
      title,
    });
    if (onImageLoad) {
      onImageLoad();
    }
  };
  const showPlaceholder = !displayImage && isLoading;

  // Debug: Log display state changes
  useEffect(() => {
    console.log('📊 [ImageThumbnail] Display state:', {
      imageUrl,
      imageError,
      displayImage,
      showPlaceholder,
      isLoading,
      hasImageProp: !!image,
      title,
      category,
    });
  }, [
    imageUrl,
    imageError,
    displayImage,
    showPlaceholder,
    isLoading,
    image,
    title,
    category,
  ]);

  // Debug: Log when rendering Image component
  useEffect(() => {
    if (displayImage) {
      console.log('🖼️ [ImageThumbnail] Rendering Image component with:', {
        src: displayImage,
        alt,
        title,
        category,
        unoptimized: true,
        isExternal: displayImage.startsWith('http'),
        domain: displayImage ? new URL(displayImage).hostname : null,
      });
    }
  }, [displayImage, alt, title, category]);

  const handleImageLoad = (
    event?: React.SyntheticEvent<HTMLImageElement, Event>
  ) => {
    const loadDetails = {
      imageUrl: displayImage,
      alt,
      title,
      category,
      naturalWidth: event?.currentTarget?.naturalWidth,
      naturalHeight: event?.currentTarget?.naturalHeight,
      currentSrc: event?.currentTarget?.currentSrc,
    };

    console.log('✅ [ImageThumbnail] Image loaded successfully:', loadDetails);

    if (onImageLoad) {
      onImageLoad();
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        padding: spacing[2],
        alignItems: 'center',
        gap: spacing[2],
        borderRadius,
        position: 'relative',
        width,
        height,
        minWidth: width,
        overflow: 'hidden',
        flexShrink: 0,
        marginTop: 0,
        backgroundColor: displayImage
          ? 'transparent'
          : showPlaceholder
            ? backgroundColors.secondary
            : backgroundColors.shade,
        backgroundImage: displayImage ? `url(${displayImage})` : 'none',
        backgroundPosition: '50%',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        ...style,
      }}
      className={className}
    >
      {displayImage ? (
        useFallbackImg ? (
          // Fallback to regular img tag when Next.js Image fails
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={displayImage}
            alt={alt}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              position: 'absolute',
              top: 0,
              left: 0,
            }}
            onError={handleFallbackImgError}
            onLoad={handleFallbackImgLoad}
          />
        ) : (
          <Image
            src={displayImage}
            alt={alt}
            fill
            style={{ objectFit: 'cover' }}
            onError={handleImageError}
            onLoad={handleImageLoad}
            onLoadingComplete={img => {
              console.log('✅ [ImageThumbnail] Image loading complete:', {
                src: img.src,
                naturalWidth: img.naturalWidth,
                naturalHeight: img.naturalHeight,
                currentSrc: img.currentSrc,
              });
            }}
            unoptimized={true}
          />
        )
      ) : showPlaceholder ? (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: typographyColors.tertiary,
            fontSize: typography.size.xs,
          }}
        >
          Loading...
        </div>
      ) : (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: backgroundColors.shade,
            color: typographyColors.tertiary,
            fontSize: typography.size.xs,
          }}
        >
          No Image
        </div>
      )}
    </div>
  );
};
